import Callout from "components/callout/callout"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { AccessRole } from 'types'
import useCustomers, { CustomersSortBy } from "hooks/useCustomers"
import useStorages from "hooks/useStorages"
import { FormStatus } from "@/types"
import React from "react"
import IconImage from "components/image/IconImage"
import Section from "components/layout/section"
import { ReactComponent as CustomerIcon } from "assets/icons/account_circle.svg"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import Button from "components/inputs/button"
import TableList from "components/list/tableList/tableList"
import TableListRow from "components/list/tableList/tableListRow"
import TableListCell from "components/list/tableList/tableListCell"
import useAccess from "@/hooks/useAccess"
import CustomerFilter from "./CustomerFilter"
import DocList, { ViewMode } from "components/list/docList/docList"
import Doc from "components/list/docList/doc"

interface CustomerListProps {
    storageId: string
}

const CustomerList: React.FC<CustomerListProps> = ({ storageId }) => {

    const { t } = useTranslation()

    const { storage } = useStorages({ storageId })

    const {
        customers,
        customersStatus,
        filteredCustomers,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        filterQuery,
        setFilterQuery,
        displayDeleted,
        setDisplayDeleted
    } = useCustomers({ storageId })
    
    const {
        limitToRoles
    } = useAccess({ storageId })

    // Handle error state.
    if (customersStatus === FormStatus.ERROR) {
        return <Callout type={`error`}>{t('errorLoadingCustomers')}</Callout>
    }

    // Handle empty state.
    if (customersStatus === FormStatus.SUCCESS && !storage?.counters?.customerCounter && limitToRoles([AccessRole.OWNER, AccessRole.EDITOR])) {
        return (
            <Section design={`card-centered`}>
                <IconImage>
                    <CustomerIcon />
                </IconImage>
                <h2>{t('emptyStateCustomersTitle')}</h2>
                <p>{t('emptyStateCustomersDescription')}</p>
                <Link className="block w-full md:inline-block md:w-fit" to={`/storage/customers/create`}>
                    <Button wideOnMobile design="primary">
                        <AddIcon />
                        <span>{t('buttonNewCustomer')}</span>
                    </Button>
                </Link>
            </Section>
        )
    }

    // Render the JSX.
    return (
        <DocList
            filterElement={
                <CustomerFilter
                    filterQuery={filterQuery}
                    setFilterQuery={setFilterQuery}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    sortDirection={sortDirection}
                    setSortDirection={setSortDirection}
                    allowedSortBy={["id", "name", "email", "phone"]}
                    displayDeleted={displayDeleted}
                    setDisplayDeleted={setDisplayDeleted}
                />
            }
            noResultsElement={
                <Callout type={`error`}>{t(`labelNoResults`)}</Callout>
            }
            showResults={(customersStatus !== `success`) || filteredCustomers.length > 0}
            id={`customers-list-filter`}
            viewModes={[ViewMode.Table]}
            defaultViewMode={ViewMode.Table}
        >
            <div data-view-mode={ViewMode.Grid} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {customersStatus === FormStatus.SUCCESS && filteredCustomers.length > 0
                    ? filteredCustomers.map((customer, key) => {    
                        const customerId = customer.id
                        if (!customerId) return null
                        return (
                            <Link key={key} to={`/storage/customers/${customerId}`}>
                                <Doc
                                    title={`${customer.customerId}: ${customer.name}`}
                                    isDeleted={customer.isDeleted}
                                >
                                    <div>{customer.contactInformation?.email}</div>
                                    <div>{customer.contactInformation?.phone}</div>
                                    {customer.notes && <div className="text-sm text-gray-500">{customer.notes.substring(0, 100)}...</div>}
                                </Doc>
                            </Link>
                        )
                    }) :
                    Array.from({ length: 3 }).map((_, _index) => (
                        <Doc key={_index} isLoading={true}>
                            <p className="skeleton"></p>
                            <p className="skeleton"></p>
                            <p className="skeleton"></p>
                        </Doc>
                    ))
                }
            </div>
            <div data-view-mode={ViewMode.Table}>
                <TableList>
                    <TableListRow isHeader={true}>
                        <TableListCell>{t(`labelId`)}</TableListCell>
                        <TableListCell>{t(`columnName`)}</TableListCell>
                        <TableListCell>{t(`columnEmail`)}</TableListCell>
                        <TableListCell>{t(`columnPhone`)}</TableListCell>
                    </TableListRow>
                    {customersStatus === FormStatus.SUCCESS && filteredCustomers.length > 0
                        ? filteredCustomers.map((customer, key) => {
                            const customerId = customer.id
                            if (!customerId) return null
                            return (
                                <TableListRow key={key} to={`/storage/customers/${customerId}`} isDeleted={customer.isDeleted}>
                                    <TableListCell>{customer.customerId}</TableListCell>
                                    <TableListCell>{customer.name}</TableListCell>
                                    <TableListCell>{customer.contactInformation?.email}</TableListCell>
                                    <TableListCell>{customer.contactInformation?.phone}</TableListCell>
                                </TableListRow>
                            )
                        }) :
                        Array.from({ length: 3 }).map((_, _index) => (
                            <TableListRow key={_index}>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                            </TableListRow>
                        ))
                    }
                </TableList>
            </div>
            <div data-view-mode={ViewMode.List} className="flex flex-col gap-4">
                {customersStatus === FormStatus.SUCCESS && filteredCustomers.length > 0
                    ? filteredCustomers.map((customer, key) => {    
                        const customerId = customer.id
                        if (!customerId) return null
                        return (
                            <Link key={key} to={`/storage/customers/${customerId}`}>
                                <Doc
                                    title={`${customer.customerId}: ${customer.name}`}
                                    isDeleted={customer.isDeleted}
                                >
                                    <div>{customer.contactInformation?.email}</div>
                                    <div>{customer.contactInformation?.phone}</div>
                                    {customer.notes && <div className="text-sm text-gray-500">{customer.notes.substring(0, 100)}...</div>}
                                </Doc>
                            </Link>
                        )
                    }) :
                    Array.from({ length: 3 }).map((_, _index) => (
                        <Doc key={_index} isLoading={true}>
                            <p className="skeleton"></p>
                            <p className="skeleton"></p>
                            <p className="skeleton"></p>
                        </Doc>
                    ))
                }
            </div>
        </DocList>
    )
}

export default CustomerList

